import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { QRCodeSVG } from "qrcode.react";
import { userService } from "../core/services";
import { AppDispatch, RootState } from "../core/redux/store";
import { setUser } from "../core/redux/userSlice";
import { utils } from "../core/helpers";
import ImageWithBasePath from "../core/common/imageWithBasePath";

type QrStatus =
  | "idle"
  | "pending"
  | "scanned"
  | "approved"
  | "rejected"
  | "expired";

type QrLoginData = {
  token: string;
  qrPayload: string;
  expiresAt: string;
  expiresIn: number;
  checkUrl?: string;
};

const QrLogin: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.user);

  const [qrStatus, setQrStatus] = useState<QrStatus>("idle");
  const [qrData, setQrData] = useState<QrLoginData | null>(null);
  const [qrCountdown, setQrCountdown] = useState<number>(0);
  const [qrError, setQrError] = useState<string>("");
  const [isGeneratingQr, setIsGeneratingQr] = useState<boolean>(false);

  useEffect(() => {
    if (!!utils.getPasswordToken() && !!user) {
      navigate("/");
    }
  }, [navigate, user]);

  const formatSeconds = (seconds: number) => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
    const mins = Math.floor(safeSeconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = Math.floor(safeSeconds % 60)
      .toString()
      .padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const handleApproved = useCallback(
    (payload: { access_token?: string; user?: any }) => {
      if (!payload?.access_token || !payload?.user) {
        setQrError("Missing user data. Please generate a new QR.");
        setQrStatus("expired");
        return;
      }

      utils.setStore("passwordToken", payload.access_token);
      dispatch(setUser(payload.user));
      setQrStatus("approved");
      navigate("/");
    },
    [dispatch, navigate]
  );

  const fetchQrLogin = useCallback(async () => {
    setIsGeneratingQr(true);
    setQrError("");
    try {
      const response = await userService.startQrLogin();
      setQrData({
        token: response.token,
        qrPayload: response.qr_payload || response.token,
        expiresAt: response.expires_at,
        expiresIn: response.expires_in,
        checkUrl: response.check_url,
      });
      setQrStatus(response.status || "pending");
      setQrCountdown(response.expires_in);
    } catch (error) {
      setQrError("Could not generate QR. Please try again.");
      setQrStatus("expired");
    } finally {
      setIsGeneratingQr(false);
    }
  }, []);

  const pollQrStatus = useCallback(async () => {
    if (!qrData?.token) return;

    try {
      const response = await userService.checkQrLoginStatus(qrData.token);
      const nextStatus = (response?.status as QrStatus) || qrStatus;

      if (response?.expires_at) {
        setQrData((prev) =>
          prev
            ? {
                ...prev,
                expiresAt: response.expires_at || prev.expiresAt,
                expiresIn:
                  typeof response.expires_in === "number"
                    ? response.expires_in
                    : prev.expiresIn,
              }
            : prev
        );
      }

      if (typeof response?.expires_in === "number") {
        setQrCountdown(response.expires_in);
        if (response.expires_in <= 0 && nextStatus !== "approved") {
          setQrStatus("expired");
          return;
        }
      }

      if (nextStatus && nextStatus !== qrStatus) {
        setQrStatus(nextStatus);
      }

      if (nextStatus === "approved") {
        handleApproved(response);
      } else if (nextStatus === "rejected") {
        setQrError("Rejected. Please generate a new QR.");
      } else if (nextStatus === "expired") {
        setQrError("");
      }
    } catch (error) {
      setQrError("Network error. Retrying...");
    }
  }, [qrData?.token, qrStatus, handleApproved]);

  useEffect(() => {
    fetchQrLogin();
  }, [fetchQrLogin]);

  useEffect(() => {
    if (!qrData?.expiresAt) return;

    const updateCountdown = () => {
      const expiresAtMs = new Date(qrData.expiresAt).getTime();
      const secondsLeft = Math.max(
        0,
        Math.floor((expiresAtMs - Date.now()) / 1000)
      );

      setQrCountdown(secondsLeft);

      if (secondsLeft <= 0 && qrStatus !== "approved") {
        setQrStatus("expired");
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [qrData?.expiresAt, qrStatus]);

  useEffect(() => {
    if (!qrData?.token) return;
    if (
      qrStatus === "approved" ||
      qrStatus === "rejected" ||
      qrStatus === "expired"
    ) {
      return;
    }

    const interval = setInterval(() => {
      pollQrStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, [qrData?.token, qrStatus, pollQrStatus]);

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <>
      <div className="main-wrapper">
        <div className="login-content">
          <div className="row">
            {/* Login Banner */}
            <div className="col-md-6 login-bg d-none d-md-flex">
              <div
                className="login-banner-content"
                style={{
                  marginLeft: "15%",
                }}
              >
                <div className="login-banner">
                  <ImageWithBasePath
                    src="assets/img/auth/auth-1.svg"
                    className="img-fluid"
                    alt="Welcome Banner"
                  />
                </div>
                <div className="mentor-course text-center">
                  <h3 className="mb-2">
                    ApiLingua <br />
                    Adaptive English Learning Platform
                  </h3>
                </div>
              </div>
            </div>
            {/* /Login Banner */}

            <div className="col-md-6 login-wrap-bg">
              {/* Login Form Wrapper */}
              <div className="login-wrapper">
                <div className="loginbox">
                  <div className="w-100">
                    <h1 className="fs-32 fw-bold topic">Sign in with QR</h1>
                    <div className="text-muted mb-3">
                      Scan the QR code with your mobile app to sign in securely.
                    </div>

                    <div className="card">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                          <div>
                            <h5 className="mb-1">QR Code</h5>
                            <div className="text-muted">
                              Waiting for you to scan and approve.
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={fetchQrLogin}
                            disabled={isGeneratingQr}
                          >
                            {isGeneratingQr
                              ? "Generating..."
                              : "Generate new QR"}
                          </button>
                        </div>

                        {qrError && (
                          <div
                            className="alert alert-warning py-2"
                            role="alert"
                          >
                            {qrError}
                          </div>
                        )}

                        <div className="text-center">
                          {qrData?.qrPayload && qrStatus !== "expired" ? (
                            <QRCodeSVG
                              value={qrData.qrPayload}
                              size={220}
                              includeMargin
                            />
                          ) : (
                            <div
                              className="text-muted text-center"
                              style={{ width: 180 }}
                            >
                              QR unavailable
                            </div>
                          )}
                        </div>
                        <div className="text-center mt-2">
                          <div className="fw-semibold mb-2">
                            {qrStatus === "pending" && "Waiting for scan…"}
                            {qrStatus === "scanned" &&
                              "Scanned, waiting for approval…"}
                            {qrStatus === "rejected" && "Rejected"}
                            {qrStatus === "expired" && "Expired"}
                            {qrStatus === "approved" &&
                              "Approved! Redirecting…"}
                            {qrStatus === "idle" && "Generating QR…"}
                          </div>
                          {qrStatus !== "approved" && qrStatus !== "expired" && (
                            <div className="text-muted mb-2">
                              Expires in {formatSeconds(qrCountdown)}
                            </div>
                          )}
                          {(qrStatus === "expired" ||
                            qrStatus === "rejected") && (
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              onClick={fetchQrLogin}
                              disabled={isGeneratingQr}
                            >
                              Generate new QR
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-center align-items-center my-3">
                      <a
                        href="https://apps.apple.com/us/app/apilingua-learn-english/id6445865305"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ImageWithBasePath
                          src="assets/img/app-stores/apple-store.svg"
                          alt="Download on the App Store"
                          className="img-fluid"
                          style={{ height: 40 }}
                        />
                      </a>
                      <a
                        href="https://play.google.com/store/apps/details?id=com.learnenaglish.mobile"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ImageWithBasePath
                          src="assets/img/app-stores/google-store.svg"
                          alt="Get it on Google Play"
                          className="img-fluid"
                          style={{ height: 60 }}
                        />
                      </a>
                    </div>

                    <div className="d-flex justify-content-center align-items-center my-3">
                      <button
                        type="button"
                        className="btn btn-outline-dark btn-lg"
                        onClick={handleBackToLogin}
                      >
                        Back to password login
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* /Login Form Wrapper */}
            </div>
          </div>
        </div>
      </div>
      {/* /Main Wrapper */}
    </>
  );
};

export default QrLogin;
